// ============================================================================
// BP-13-02: Admin Moderation Types
// BrightPath Platform - Admin Frontend
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Moderation Enums
// =============================================================================

export const ModerationItemTypeEnum = z.enum([
  'forum_post',
  'forum_reply',
  'product_review',
  'vendor_review',
  'resource_comment',
  'user_profile',
  'vendor_profile',
  'product_listing',
]);
export type ModerationItemType = z.infer<typeof ModerationItemTypeEnum>;

export const ModerationStatusEnum = z.enum([
  'pending',
  'approved',
  'rejected',
  'flagged',
  'escalated',
]);
export type ModerationStatus = z.infer<typeof ModerationStatusEnum>;

export const ModerationActionEnum = z.enum([
  'approve',
  'reject',
  'flag',
  'escalate',
  'delete',
  'edit',
  'warn_user',
]);
export type ModerationAction = z.infer<typeof ModerationActionEnum>;

export const FlagReasonEnum = z.enum([
  'spam',
  'inappropriate_content',
  'harassment',
  'misinformation',
  'off_topic',
  'copyright_violation',
  'personal_information',
  'other',
]);
export type FlagReason = z.infer<typeof FlagReasonEnum>;

export const VendorVerificationStatusEnum = z.enum([
  'pending',
  'in_review',
  'approved',
  'rejected',
  'suspended',
]);
export type VendorVerificationStatus = z.infer<typeof VendorVerificationStatusEnum>;

// =============================================================================
// Moderation Item Schemas
// =============================================================================

export const ModerationItemSchema = z.object({
  id: z.string().uuid(),
  itemType: ModerationItemTypeEnum,
  itemId: z.string().uuid(),
  status: ModerationStatusEnum,
  authorId: z.string().uuid(),
  authorName: z.string(),
  authorEmail: z.string().email(),
  title: z.string().nullable(),
  content: z.string(),
  contentPreview: z.string(),
  flagReason: FlagReasonEnum.nullable(),
  flaggedBy: z.string().uuid().nullable(),
  flaggedAt: z.string().datetime().nullable(),
  flagCount: z.number(),
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  reviewNotes: z.string().nullable(),
  createdAt: z.string().datetime(),
  metadata: z.record(z.unknown()).nullable(),
});
export type ModerationItem = z.infer<typeof ModerationItemSchema>;

export const ModerationQueueItemSchema = z.object({
  id: z.string().uuid(),
  itemType: ModerationItemTypeEnum,
  itemId: z.string().uuid(),
  status: ModerationStatusEnum,
  authorName: z.string(),
  contentPreview: z.string(),
  flagReason: FlagReasonEnum.nullable(),
  flagCount: z.number(),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  createdAt: z.string().datetime(),
  waitTime: z.string(), // "2 hours ago" format
});
export type ModerationQueueItem = z.infer<typeof ModerationQueueItemSchema>;

// =============================================================================
// Moderation Actions
// =============================================================================

export const ModerationActionRequestSchema = z.object({
  action: ModerationActionEnum,
  notes: z.string().optional(),
  notifyUser: z.boolean().default(true),
  warningMessage: z.string().optional(),
  editedContent: z.string().optional(),
});
export type ModerationActionRequest = z.infer<typeof ModerationActionRequestSchema>;

export const ModerationHistorySchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  action: ModerationActionEnum,
  performedBy: z.string().uuid(),
  performedByName: z.string(),
  notes: z.string().nullable(),
  previousStatus: ModerationStatusEnum,
  newStatus: ModerationStatusEnum,
  createdAt: z.string().datetime(),
});
export type ModerationHistory = z.infer<typeof ModerationHistorySchema>;

// =============================================================================
// Vendor Verification
// =============================================================================

export const VendorVerificationSchema = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  vendorEmail: z.string().email(),
  vendorPhone: z.string().nullable(),
  vendorWebsite: z.string().nullable(),
  businessName: z.string(),
  businessType: z.string(),
  status: VendorVerificationStatusEnum,
  submittedAt: z.string().datetime(),
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  reviewNotes: z.string().nullable(),
  documents: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
    url: z.string(),
    uploadedAt: z.string().datetime(),
  })),
  businessLicense: z.string().nullable(),
  insuranceCertificate: z.string().nullable(),
  taxId: z.string().nullable(),
  yearsInBusiness: z.number().nullable(),
  categories: z.array(z.string()),
  serviceAreas: z.array(z.string()),
  notes: z.string().nullable(),
});
export type VendorVerification = z.infer<typeof VendorVerificationSchema>;

export const VendorVerificationListItemSchema = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  businessName: z.string(),
  status: VendorVerificationStatusEnum,
  submittedAt: z.string().datetime(),
  documentCount: z.number(),
  categories: z.array(z.string()),
});
export type VendorVerificationListItem = z.infer<typeof VendorVerificationListItemSchema>;

export const VendorVerificationActionSchema = z.object({
  action: z.enum(['approve', 'reject', 'request_info']),
  notes: z.string().optional(),
  notifyVendor: z.boolean().default(true),
  requestedDocuments: z.array(z.string()).optional(),
});
export type VendorVerificationAction = z.infer<typeof VendorVerificationActionSchema>;

// =============================================================================
// Request/Response Schemas
// =============================================================================

export const ModerationQueueParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  itemType: ModerationItemTypeEnum.optional(),
  status: ModerationStatusEnum.optional(),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
  sortBy: z.enum(['createdAt', 'flagCount', 'priority']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type ModerationQueueParams = z.infer<typeof ModerationQueueParamsSchema>;

export const ModerationQueueResponseSchema = z.object({
  items: z.array(ModerationQueueItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
  pendingCount: z.number(),
  flaggedCount: z.number(),
  escalatedCount: z.number(),
});
export type ModerationQueueResponse = z.infer<typeof ModerationQueueResponseSchema>;

export const VendorVerificationListParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  status: VendorVerificationStatusEnum.optional(),
  sortBy: z.enum(['submittedAt', 'vendorName']).default('submittedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type VendorVerificationListParams = z.infer<typeof VendorVerificationListParamsSchema>;

export const VendorVerificationListResponseSchema = z.object({
  items: z.array(VendorVerificationListItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type VendorVerificationListResponse = z.infer<typeof VendorVerificationListResponseSchema>;

// =============================================================================
// Moderation Statistics
// =============================================================================

export const ModerationStatsSchema = z.object({
  totalPending: z.number(),
  totalFlagged: z.number(),
  totalEscalated: z.number(),
  resolvedToday: z.number(),
  resolvedThisWeek: z.number(),
  averageResolutionTime: z.string(), // "2h 30m" format
  byItemType: z.record(ModerationItemTypeEnum, z.object({
    pending: z.number(),
    approved: z.number(),
    rejected: z.number(),
  })),
  topFlagReasons: z.array(z.object({
    reason: FlagReasonEnum,
    count: z.number(),
  })),
  pendingVendorVerifications: z.number(),
});
export type ModerationStats = z.infer<typeof ModerationStatsSchema>;
